"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { m, AnimatePresence } from "motion/react";
import {
    IconSearch,
    IconTargetArrow,
    IconHeartHandshake,
    IconRocket,
    IconStars,
} from "@tabler/icons-react";
import { cn } from "@/lib/utils";

const steps = [
    {
        id: "entende",
        label: "Entende",
        step: "Passo 01",
        icon: IconSearch,
        description: "Diagnóstico profundo do negócio, mercado e concorrência com base em dados reais.",
    },
    {
        id: "conecta",
        label: "Atrai",
        step: "Passo 02",
        icon: IconTargetArrow,
        description: "Campanhas de tráfego, criativos e mídia massiva que geram demanda real.",
    },
    {
        id: "ajuda",
        label: "Nutre",
        step: "Passo 03",
        icon: IconHeartHandshake,
        description: "Automações e páginas que qualificam leads e reduzem objeções antes da venda.",
    },
    {
        id: "vende",
        label: "Converte",
        step: "Passo 04",
        icon: IconRocket,
        description: "CRM, dashboards e processo comercial que tornam a conversão previsível e escalável.",
    },
    {
        id: "otimiza",
        label: "Retém",
        step: "Passo 05",
        icon: IconStars,
        description: "Indicações e retenção que transformam sua base em seu melhor ativo de marketing.",
    },
];

function describeArc(
    x: number,
    y: number,
    radius: number,
    innerRadius: number,
    startAngle: number,
    endAngle: number,
) {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);
    const innerStart = polarToCartesian(x, y, innerRadius, endAngle);
    const innerEnd = polarToCartesian(x, y, innerRadius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
        "M", start.x, start.y,
        "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
        "L", innerEnd.x, innerEnd.y,
        "A", innerRadius, innerRadius, 0, largeArcFlag, 1, innerStart.x, innerStart.y,
        "Z"
    ].join(" ");
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

export const TechVisual = () => {
    // Auto-cycle state
    const [activeIndex, setActiveIndex] = useState(0);
    // When user interacts (hover/touch), we pause auto-cycle and track the override
    const [userIndex, setUserIndex] = useState<number | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // The "displayed" index: user override wins over auto-cycle
    const displayIndex = userIndex !== null ? userIndex : activeIndex;

    const startCycle = useCallback(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % steps.length);
        }, 2200);
    }, []);

    // Start auto-cycle on mount, and restart when user releases
    useEffect(() => {
        startCycle();
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, [startCycle]);

    const handleEnter = (index: number) => {
        // Pause cycle, set user override
        if (intervalRef.current) clearInterval(intervalRef.current);
        setUserIndex(index);
    };

    const handleLeave = () => {
        setUserIndex(null);
        startCycle();
    };

    const VIEWBOX_SIZE = 600;
    const CENTER = VIEWBOX_SIZE / 2;
    const OUTER_RADIUS = 195;
    const INNER_RADIUS = 105;
    const ANGLE_PER_STEP = 360 / steps.length;
    const GAP_DEGREES = 3;
    const INITIAL_ROTATION = -18;

    return (
        <div className="relative w-full h-full min-h-[400px] flex items-center justify-center bg-black overflow-hidden font-geist select-none">

            {/* Outer faint dashed spinning ring */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-15 -translate-y-[50px]">
                <div className="w-[340px] h-[340px] rounded-full border border-dashed border-neutral-400 animate-[spin_80s_linear_infinite]" />
            </div>

            {/* Central info panel: z-index higher than the wheel to prevent overlap */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30 -translate-y-[50px]">
                <AnimatePresence mode="wait">
                    <m.div
                        key={displayIndex}
                        initial={{ opacity: 0, scale: 0.85, y: 8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: -8 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="text-center px-6 max-w-[140px]"
                    >
                        <span className="text-midas-yellow-500 font-mono text-[9px] uppercase tracking-[3px] block mb-1.5 drop-shadow-md">
                            {steps[displayIndex].step}
                        </span>
                        <span className="text-white font-bold text-xl leading-tight mb-1.5 drop-shadow-lg block">
                            {steps[displayIndex].label}
                        </span>
                        <p className="text-neutral-400 text-[10px] leading-snug drop-shadow-lg">
                            {steps[displayIndex].description}
                        </p>
                    </m.div>
                </AnimatePresence>
            </div>

            {/* SVG Wheel Backgrounds */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[calc(50%+50px)] w-[450px] h-[450px] flex items-center justify-center z-10 scale-[0.65] xs:scale-[0.75] sm:scale-90 md:scale-100 lg:scale-[1.12]">
                <svg
                    width="100%"
                    height="100%"
                    viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
                    className="overflow-visible"
                    style={{ transform: `rotate(${INITIAL_ROTATION}deg)` }}
                >
                    <defs>
                        <linearGradient id="tv-gold" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#f5c518" />
                            <stop offset="100%" stopColor="#c8930a" />
                        </linearGradient>
                    </defs>

                    {steps.map((step, index) => {
                        const startAngle = index * ANGLE_PER_STEP + GAP_DEGREES / 2;
                        const endAngle = (index + 1) * ANGLE_PER_STEP - GAP_DEGREES / 2;
                        const midAngle = (startAngle + endAngle) / 2;

                        const isActive = displayIndex === index;
                        const outerR = isActive ? OUTER_RADIUS + 10 : OUTER_RADIUS;
                        const arcPath = describeArc(CENTER, CENTER, outerR, INNER_RADIUS, startAngle, endAngle);
                        const dotPos = polarToCartesian(CENTER, CENTER, 188, midAngle);

                        return (
                            <g
                                key={`slice-${step.id}`}
                                onMouseEnter={() => handleEnter(index)}
                                onMouseLeave={handleLeave}
                                onTouchStart={() => handleEnter(index)}
                                className="cursor-pointer"
                                style={{ touchAction: "none" }} // prevent scroll while touching the wheel
                            >
                                {/* Slice path — base layer (always dark) */}
                                <path
                                    d={arcPath}
                                    fill="rgba(22,22,22,1)"
                                    className="stroke-neutral-800 stroke-1"
                                />
                                {/* Slice path — gold overlay (opacity animated) */}
                                <m.path
                                    d={arcPath}
                                    fill="url(#tv-gold)"
                                    initial={false}
                                    animate={{
                                        opacity: isActive ? 1 : 0,
                                        filter: isActive
                                            ? "drop-shadow(0px 0px 18px rgba(245,197,24,0.45))"
                                            : "none",
                                    }}
                                    transition={{ duration: 0.35, ease: "easeOut" }}
                                    className="stroke-neutral-800 stroke-1"
                                />

                                {/* Connection Dot on dashed ring */}
                                <m.circle
                                    cx={dotPos.x}
                                    cy={dotPos.y}
                                    r="4"
                                    initial={false}
                                    animate={{
                                        fill: isActive ? "#f5c518" : "#333",
                                        scale: isActive ? 1.6 : 1,
                                    }}
                                    transition={{ duration: 0.3 }}
                                />
                            </g>
                        );
                    })}
                </svg>

                {/* HTML Overlay for Icons & Labels (solves Safari/foreignObject bugs) */}
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{ transform: `rotate(${INITIAL_ROTATION}deg)` }}
                >
                    {steps.map((step, index) => {
                        const startAngle = index * ANGLE_PER_STEP + GAP_DEGREES / 2;
                        const endAngle = (index + 1) * ANGLE_PER_STEP - GAP_DEGREES / 2;
                        const midAngle = (startAngle + endAngle) / 2;

                        const isActive = displayIndex === index;

                        const iconDist = INNER_RADIUS + (OUTER_RADIUS - INNER_RADIUS) / 2;
                        const iconPos = polarToCartesian(CENTER, CENTER, iconDist, midAngle);

                        const textDist = OUTER_RADIUS + 44;
                        const textPos = polarToCartesian(CENTER, CENTER, textDist, midAngle);

                        const Icon = step.icon;

                        return (
                            <div key={`html-${step.id}`}>
                                {/* Icon Container (Counter-Rotate so icons stand upright) */}
                                <m.div
                                    className="absolute flex items-center justify-center pointer-events-auto"
                                    style={{
                                        left: `${(iconPos.x / VIEWBOX_SIZE) * 100}%`,
                                        top: `${(iconPos.y / VIEWBOX_SIZE) * 100}%`,
                                    }}
                                    animate={{ scale: isActive ? 1.3 : 1 }}
                                    transition={{ duration: 0.3 }}
                                    onMouseEnter={() => handleEnter(index)}
                                    onMouseLeave={handleLeave}
                                    onTouchStart={() => handleEnter(index)}
                                >
                                    <div
                                        className="relative flex items-center justify-center -translate-x-1/2 -translate-y-1/2 rounded-full transition-colors duration-300"
                                        style={{
                                            width: 48,
                                            height: 48,
                                            backgroundColor: isActive ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.04)",
                                            transform: `translate(-50%, -50%) rotate(${-INITIAL_ROTATION}deg)` // Upright
                                        }}
                                    >
                                        <Icon
                                            size={22}
                                            style={{
                                                color: isActive ? "#ffffff" : "#666",
                                                transition: "color 0.3s"
                                            }}
                                        />
                                    </div>
                                </m.div>

                                {/* Title Label (Counter-Rotate so it stands upright) */}
                                <m.div
                                    className="absolute flex flex-col items-center justify-center pointer-events-none whitespace-nowrap"
                                    style={{
                                        left: `${(textPos.x / VIEWBOX_SIZE) * 100}%`,
                                        top: `${(textPos.y / VIEWBOX_SIZE) * 100}%`,
                                    }}
                                    initial={false}
                                    animate={{
                                        opacity: isActive ? 1 : 0.4,
                                        y: isActive ? -4 : 0,
                                    }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div
                                        className="flex flex-col items-center"
                                        style={{ transform: `translate(-50%, -50%) rotate(${-INITIAL_ROTATION}deg)` }}
                                    >
                                        <span className="font-mono tracking-widest text-[10px] text-midas-yellow-500/90 mb-[2px]">
                                            {step.step}
                                        </span>
                                        <span
                                            className="font-bold text-[14px]"
                                            style={{ color: isActive ? "#ffffff" : "#9ca3af", transition: "color 0.3s" }}
                                        >
                                            {step.label}
                                        </span>
                                    </div>
                                </m.div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
